import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  PLATFORM_ID,
  inject,
  computed,
  effect,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '@pedix-workspace/pedixapp-core-services';
import { StockService } from '@pedix-workspace/pedixapp-stock';
import { Router, ActivatedRoute } from '@angular/router';
import {
  CartItem,
  isCategoryAvailable,
  PurchaseRestrictionKind,
  getRestrictionConflictViolations,
  getSelectionRestrictionViolations,
  formatRestrictionNames,
  toRestrictionSelection,
} from '@pedix-workspace/utils';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import { ModalDialogComponent, ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import { EstablishmentWorkingHoursService } from '../../main/establishment-working-hours.service';
import { ApiV1_Category } from '@pedix-workspace/api-v1';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { IconAlertComponent, IconBackComponent } from '@pedix-workspace/angular-ui-icons';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AppRequestContextService } from '../../../app-request-context.service';
import { AppCartItemsService } from '../../../app-cart-items.service';
import { EstablishmentsCheckoutService } from '../../order-checkout/establishments-checkout.service';
import { CheckoutPreloaderService } from '../../order-checkout/checkout-preloader.service';
import {
  CartViewComponent,
  EndOrderTotalsService,
  OrderTotalsResumeComponent,
  EndOrderFormService,
  ProductRestrictionAlertComponent,
} from '@pedix-workspace/angular-shared-order';
import { environment } from 'apps/pedixapp/src/environments/environment';
import { CartPromotionsService } from '../../../cart-promotions.service';
import { WorkingHoursMessageDialogComponent } from '@pedix-workspace/pedixapp-shared-catalog';
import { REPEAT_ORDER_REMOVED_NAMES_STATE_KEY } from '../repeat-order/repeat-order-state.service';

const RESTRICTED_BY_SELECTION_LABEL_KEYS: Record<PurchaseRestrictionKind, string> = {
  shipping: 'cartView.productRestrictedByShipping',
  payment: 'cartView.productRestrictedByPayment',
  both: 'cartView.productRestrictedByBoth',
};

@Component({
  selector: 'app-resume',
  templateUrl: './resume.component.html',
  styleUrls: ['./resume.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    IconBackComponent,
    IconAlertComponent,
    ModalDialogComponent,
    AlertComponent,
    OrderTotalsResumeComponent,
    CartViewComponent,
    ProductRestrictionAlertComponent,
  ],
})
export class ResumeComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private analytics = inject(AnalyticsService);
  private appRequestContext = inject(AppRequestContextService);
  private stockService = inject(StockService);
  private establishmentWorkingHours = inject(EstablishmentWorkingHoursService);
  private appCartItemsService = inject(AppCartItemsService);
  private checkoutPreloaderService = inject(CheckoutPreloaderService);
  private establishmentsCheckoutService = inject(EstablishmentsCheckoutService);
  private t = inject(TranslocoService);
  private endOrderFormService = inject(EndOrderFormService);

  private readonly checkoutSelection = toRestrictionSelection(this.endOrderFormService.sessionData);
  private endOrderTotalsService = inject(EndOrderTotalsService);
  private cartPromotionsService = inject(CartPromotionsService);
  private modalDialogService = inject(ModalDialogService);

  readonly removeCartItemDialog = viewChild.required<ModalDialogComponent>('removeCartItemDialog');

  readonly cartItemToRemove = signal<CartItem | null>(null);

  readonly repeatOrderRemovedNames = this.readRepeatOrderRemovedNames();

  readonly openedStatus = toSignal(this.establishmentWorkingHours.openedStatus$);
  readonly isCartDisabled = computed(() => this.openedStatus()?.isCartDisabled ?? false);

  readonly loadingCheckout = signal(false);

  readonly establishment = computed(() => this.appRequestContext.establishment());
  readonly selectedLanguage = computed(() => this.appRequestContext.selectedLanguage());
  readonly cartItems = computed(() => this.appCartItemsService.cartItems());
  readonly endOrderTotals = computed(() =>
    this.endOrderTotalsService.getEndOrderTotals({
      cartItems: this.cartItems(),
      foodBankDonation: environment.foodBankDonationAmount,
      currencyCode: this.establishment().currencyCode,
      promotionDiscount: this.cartPromotionsService.totalDiscount(),
    }),
  );
  readonly stockErrors = computed(() => this.stockService.stock().getItemsInCartWithStockErrors());

  readonly hasProductsWithDisabledCategories = computed(() => {
    return this.cartItems().some(cartItem => {
      const category = this.appRequestContext
        .allCategories()
        .find(category => category.id === cartItem.category.id);

      if (!category) {
        return true;
      }
      return !category.active || !isCategoryAvailable(category.availability);
    });
  });
  readonly hasStockErrors = computed(() => this.stockErrors().length > 0);
  readonly cartProducts = computed(() => this.cartItems().map(cartItem => cartItem.product));
  readonly restrictionConflicts = computed(() =>
    getRestrictionConflictViolations(
      this.cartProducts(),
      this.establishmentsCheckoutService.purchaseOptions(),
    ),
  );
  readonly hasRestrictionConflicts = computed(() => this.restrictionConflicts().length > 0);
  readonly selectionViolations = computed(() =>
    getSelectionRestrictionViolations(
      this.cartProducts(),
      this.checkoutSelection,
      this.establishmentsCheckoutService.purchaseOptions(),
    ),
  );
  readonly hasSelectionViolations = computed(() => this.selectionViolations().length > 0);
  readonly restrictionLabelByProductId = computed(() => {
    const labels = new Map<string, string>();

    for (const violation of this.selectionViolations()) {
      labels.set(
        violation.productId,
        this.t.translate(RESTRICTED_BY_SELECTION_LABEL_KEYS[violation.kind]),
      );
    }
    const orLabel = this.t.translate('global.or');

    for (const conflict of this.restrictionConflicts()) {
      const conditions = [
        conflict.shipping &&
          this.t.translate('productRestriction.onlyShipping', {
            names: formatRestrictionNames(conflict.shipping, orLabel),
          }),
        conflict.payment &&
          this.t.translate('productRestriction.onlyPayment', {
            names: formatRestrictionNames(conflict.payment, orLabel),
          }),
      ].filter(Boolean);

      labels.set(conflict.productId, conditions.join(' · '));
    }
    return labels;
  });
  readonly disableConfirmOrder = computed(() => {
    const hasMinimumPurchaseError = this.hasMinimumPurchaseError();
    const hasProductsWithDisabledCategories = this.hasProductsWithDisabledCategories();
    const hasStockErrors = this.hasStockErrors();

    if (
      hasMinimumPurchaseError ||
      hasProductsWithDisabledCategories ||
      hasStockErrors ||
      this.hasRestrictionConflicts() ||
      this.appCartItemsService.isCartEmpty()
    ) {
      return true;
    }
    return false;
  });
  readonly minimumPurchase = computed(
    () => this.establishment().catalogConfiguration.minimumPurchase,
  );
  readonly hasMinimumPurchaseError = computed(
    () =>
      this.minimumPurchase() &&
      this.minimumPurchase() > (this.endOrderTotals()?.totalAmountWithDiscount || 0),
  );
  readonly displaySku = computed(() => this.appRequestContext.shouldDisplaySku());
  readonly participatingPromotionsByProductId =
    this.cartPromotionsService.participatingPromotionsByProductId;
  readonly savingsByProductId = this.cartPromotionsService.savingsByProductId;
  readonly upsellHintsByCartItemId = this.cartPromotionsService.upsellHintsByCartItemId;
  readonly categoriesAvailabilityById = computed(() =>
    this.appRequestContext
      .allCategories()
      .reduce<Record<string, ApiV1_Category['availability']>>((map, category) => {
        map[category.id] = category.availability;
        return map;
      }, {}),
  );

  constructor() {
    effect(() => {
      if (
        this.appCartItemsService.isCartEmpty() &&
        isPlatformBrowser(this.platformId) &&
        !this.repeatOrderRemovedNames
      ) {
        this.back();
      }
    });
  }

  ngOnInit(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.checkoutPreloaderService.preloadCheckout();
  }

  private readRepeatOrderRemovedNames(): string {
    const navigation = this.router.currentNavigation() ?? this.router.lastSuccessfulNavigation();
    const removedNames = navigation?.extras.state?.[REPEAT_ORDER_REMOVED_NAMES_STATE_KEY];

    return Array.isArray(removedNames) ? removedNames.join(', ') : '';
  }

  back() {
    this.router.navigate(['../'], { relativeTo: this.activatedRoute });
  }

  onEditCartItem(cartItem: CartItem) {
    this.router.navigate([`../resumen/edicion/${cartItem.id}`], {
      relativeTo: this.activatedRoute,
    });
  }

  onRemoveCartItem(cartItem: CartItem) {
    this.cartItemToRemove.set(cartItem);

    this.removeCartItemDialog().open(async () => this.appCartItemsService.removeCartItem(cartItem));
  }

  onRemoveCartItems(cartItems: CartItem[]) {
    cartItems.forEach(cartItem => this.appCartItemsService.removeCartItem(cartItem));
  }

  onUpdateCartItem(cartItem: CartItem) {
    this.appCartItemsService.updateCartItem(cartItem);
  }

  goToEndOrder() {
    if (this.disableConfirmOrder()) {
      return;
    }
    this.loadingCheckout.set(true);

    this.analytics.initiateCheckout(this.cartItems(), this.endOrderTotals()?.finalAmount || 0);

    this.router.navigate([`../pedido/`], { relativeTo: this.activatedRoute });
  }

  displayWorkingHours(event: Event) {
    event.stopPropagation();

    this.modalDialogService
      .attachDialog(WorkingHoursMessageDialogComponent)
      .open(this.establishment().workingDays);
  }
}
